import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, Check, Pencil, Plus, X } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Entrada, Selecao } from '../../componentes/formulario';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { rotuloFunilPipeline } from '../../nucleo/rotulos';
import type { Funil } from './tipos';

/** U13: os funis ativos (o padrão primeiro), para o seletor do quadro e o "copiar etapas de". */
export function useFunis() {
  return useQuery({ queryKey: ['pipeline', 'funis'], queryFn: () => get<{ itens: Funil[] }>('/pipeline/funis') });
}

/**
 * U13: criar, renomear, arquivar e desarquivar funis (no Drawer "Funis" do quadro). O funil novo copia as etapas do funil
 * escolhido (o padrão, de início); as etapas de cada funil se editam em Configurações > Estruturas e a ordem, arrastando as
 * colunas no quadro.
 */
export function GerirFunis({ aoCriar }: { aoCriar?: (id: string) => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [nome, setNome] = useState('');
  const [copiarDe, setCopiarDe] = useState('');
  const [editando, setEditando] = useState<{ id: string; nome: string } | null>(null);
  const [arquivando, setArquivando] = useState<Funil | null>(null);
  const q = useQuery({ queryKey: ['pipeline', 'funis', 'todos'], queryFn: () => get<{ itens: Funil[] }>('/pipeline/funis', { arquivados: 'true' }) });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline'] });
  const falha = (e: unknown) => toast.erro(mensagemDeErro(e));
  const ativos = (q.data?.itens ?? []).filter((f) => !f.arquivado_em);

  const criar = useMutation({
    mutationFn: () => post<{ id: string }>('/pipeline/funis', { nome: nome.trim(), ...(copiarDe ? { copiar_de: copiarDe } : {}) }),
    onSuccess: async (r) => {
      setNome('');
      toast.sucesso(tn('comum.salvo'));
      await recarregar();
      if (r?.id) aoCriar?.(r.id);
    },
    onError: falha,
  });
  const renomear = useMutation({
    mutationFn: (v: { id: string; nome: string }) => patch(`/pipeline/funis/${v.id}`, { nome: v.nome.trim() }),
    onSuccess: async () => {
      setEditando(null);
      toast.sucesso(tn('comum.salvo'));
      await recarregar();
    },
    onError: falha,
  });
  const arquivar = useMutation({
    mutationFn: (v: { id: string; arquivar: boolean }) => post(`/pipeline/funis/${v.id}/${v.arquivar ? 'arquivar' : 'desarquivar'}`),
    onSuccess: async () => {
      setArquivando(null);
      toast.sucesso(tn('comum.salvo'));
      await recarregar();
    },
    onError: (e) => {
      setArquivando(null);
      falha(e);
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-tinta-3">{t('funis.ajuda')}</p>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (nome.trim()) criar.mutate();
        }}
      >
        <Entrada aria-label={t('funis.nomeNovo')} placeholder={t('funis.nomeNovo')} value={nome} onChange={(e) => setNome(e.target.value)} maxLength={60} className="min-w-0 flex-1 basis-40" />
        <div className="w-full sm:w-56">
          <Selecao
            aria-label={t('funis.copiarDe')}
            opcoes={ativos.filter((f) => !f.padrao).map((f) => ({ valor: f.id, rotulo: rotuloFunilPipeline(f.nome) }))}
            vazio={`${t('funis.copiarDe')}: ${rotuloFunilPipeline(ativos.find((f) => f.padrao)?.nome ?? '')}`}
            value={copiarDe}
            onChange={(e) => setCopiarDe(e.target.value)}
          />
        </div>
        <Botao type="submit" variante="secundario" icone={<Plus className="size-4" />} carregando={criar.isPending}>
          {t('funis.criar')}
        </Botao>
      </form>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Vazio texto={t('funis.vazio')} />
          ) : (
            <ul className="divide-y divide-borda">
              {d.itens.map((f) => (
                <li key={f.id} className="flex items-start justify-between gap-3 py-2.5 text-sm sm:items-center">
                  {editando?.id === f.id ? (
                    <form
                      className="flex min-w-0 flex-1 items-center gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (editando.nome.trim()) renomear.mutate(editando);
                      }}
                    >
                      <Entrada autoFocus aria-label={t('campos.nome')} value={editando.nome} maxLength={60} onChange={(e) => setEditando({ id: f.id, nome: e.target.value })} />
                      <Botao type="submit" tamanho="sm" variante="fantasma" aria-label={tn('comum.salvar')} carregando={renomear.isPending} icone={<Check className="size-4" />} />
                      <Botao tamanho="sm" variante="fantasma" aria-label={tn('comum.cancelar')} onClick={() => setEditando(null)} icone={<X className="size-4" />} />
                    </form>
                  ) : (
                    <>
                      <span className="flex min-w-0 flex-col gap-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="min-w-0 font-medium break-words">{rotuloFunilPipeline(f.nome)}</span>
                          {f.padrao && <Selo cor="azul">{t('funis.padrao')}</Selo>}
                          {f.arquivado_em && <Selo cor="neutro">{t('funis.arquivado')}</Selo>}
                        </span>
                        <span className="numero text-xs text-tinta-3">
                          {t('funis.etapas', { count: f.etapas })} · {t('funis.abertas', { count: f.abertas })}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1">
                        <Botao tamanho="sm" variante="fantasma" aria-label={`${tn('comum.editar')}: ${rotuloFunilPipeline(f.nome)}`} onClick={() => setEditando({ id: f.id, nome: f.nome })} icone={<Pencil className="size-4" />} />
                        {!f.padrao &&
                          (f.arquivado_em ? (
                            <Botao
                              tamanho="sm"
                              variante="fantasma"
                              aria-label={`${t('funis.desarquivar')}: ${rotuloFunilPipeline(f.nome)}`}
                              title={t('funis.desarquivar')}
                              carregando={arquivar.isPending && arquivar.variables?.id === f.id}
                              onClick={() => arquivar.mutate({ id: f.id, arquivar: false })}
                              icone={<ArchiveRestore className="size-4" />}
                            />
                          ) : (
                            <Botao tamanho="sm" variante="fantasma" aria-label={`${t('funis.arquivar')}: ${rotuloFunilPipeline(f.nome)}`} title={t('funis.arquivar')} onClick={() => setArquivando(f)} icone={<Archive className="size-4" />} />
                          ))}
                      </span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )
        }
      </Estado>
      <Confirmar
        aberto={!!arquivando}
        aoFechar={() => setArquivando(null)}
        aoConfirmar={() => arquivando && arquivar.mutate({ id: arquivando.id, arquivar: true })}
        carregando={arquivar.isPending}
        titulo={t('funis.confirmarArquivar')}
        texto={t('funis.confirmarArquivarTexto')}
      />
    </div>
  );
}
